// Repair Partial Tool-Argument JSON
//
// When a streaming model response emits a tool call but the stream is
// terminated (e.g. max output tokens hit) mid-arguments, the accumulated
// `function.arguments` buffer is an incomplete JSON string — typically cut off
// inside a string literal or mid-object — and `JSON.parse` throws.
//
// This utility attempts, in order:
//   1) A direct `JSON.parse` (happy path; no truncation).
//   2) Structural repair: walk the string, track the structural stack
//      (object / array / string with escape awareness), then append the
//      minimum closing tokens to produce a parseable JSON object.
//   3) Salvage: if structural repair still fails, extract whatever top-level
//      `"key": <fully-parseable value>` pairs it can, assembling them into an
//      object. Incomplete / truncated values are simply skipped.
//
// The result always includes a `parsed` object (possibly `{}`) so callers can
// dispatch the tool without fear of a silent drop. When `wasTruncated` is
// true, tools that opt in (see `BaseAITool.getCurrentToolCallTruncation`)
// can branch on the truncation reason and surface it back to the model.

const MAX_STACK_DEPTH = 64;

/**
 * @typedef {Object} RepairResult
 * @property {object} parsed         - Always an object; `{}` if nothing was recoverable.
 * @property {boolean} wasTruncated  - True if the input wasn't a complete, valid JSON object.
 * @property {"ok"|"empty"|"unterminated-string"|"unterminated-object"|"unterminated-array"|"invalid"} reason
 * @property {string} rawArguments   - The original input string (for tools that want to inspect).
 */

/**
 * @param {string} rawArgumentsStr
 * @returns {RepairResult}
 */
export function repairPartialToolArgJson(rawArgumentsStr) {
  const rawArguments = typeof rawArgumentsStr === "string" ? rawArgumentsStr : "";
  const trimmed = rawArguments.trim();

  if (trimmed.length === 0) {
    return {
      parsed: {},
      wasTruncated: true,
      reason: "empty",
      rawArguments,
    };
  }

  // Happy path.
  try {
    const parsed = JSON.parse(trimmed);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return {
        parsed,
        wasTruncated: false,
        reason: "ok",
        rawArguments,
      };
    }
  } catch (_) {
    // Fall through to structural repair.
  }

  const structural = _structuralRepair(trimmed);
  if (structural.parsed) {
    return {
      parsed: structural.parsed,
      wasTruncated: true,
      reason: structural.reason,
      rawArguments,
    };
  }

  // Last resort: per-key salvage from the raw buffer.
  const salvaged = _salvageTopLevelKeys(trimmed);
  return {
    parsed: salvaged || {},
    wasTruncated: true,
    reason: "invalid",
    rawArguments,
  };
}

/**
 * Structural scan + close. Returns `{ parsed, reason }` on success or
 * `{ parsed: null }` if even closing doesn't yield parseable JSON.
 * @param {string} text
 */
function _structuralRepair(text) {
  if (text[0] !== "{") {
    return { parsed: null, reason: "invalid" };
  }

  const stack = [];
  let i = 0;
  let inString = false;
  let escape = false;
  let truncatedInUnicodeEscape = 0;

  while (i < text.length) {
    const ch = text[i];

    if (inString) {
      if (escape) {
        // Unicode escape needs 4 hex digits; if we don't have them, bail.
        if (ch === "u") {
          if (i + 4 >= text.length) {
            truncatedInUnicodeEscape = text.length - i;
            break;
          }
          i += 5;
          escape = false;
          continue;
        }
        escape = false;
        i += 1;
        continue;
      }
      if (ch === "\\") {
        escape = true;
        i += 1;
        continue;
      }
      if (ch === '"') {
        inString = false;
        i += 1;
        continue;
      }
      i += 1;
      continue;
    }

    if (ch === '"') {
      inString = true;
      i += 1;
      continue;
    }
    if (ch === "{") {
      if (stack.length >= MAX_STACK_DEPTH) return { parsed: null, reason: "invalid" };
      stack.push("}");
      i += 1;
      continue;
    }
    if (ch === "[") {
      if (stack.length >= MAX_STACK_DEPTH) return { parsed: null, reason: "invalid" };
      stack.push("]");
      i += 1;
      continue;
    }
    if (ch === "}" || ch === "]") {
      stack.pop();
      i += 1;
      continue;
    }
    i += 1;
  }

  // We ran out of input. Figure out why and close up.
  let repaired = text;
  let reason = "invalid";

  if (inString) {
    reason = "unterminated-string";
    // If we stopped mid-unicode-escape, drop the partial `\uXX...` tail first.
    if (truncatedInUnicodeEscape > 0) {
      repaired = repaired.slice(0, repaired.length - truncatedInUnicodeEscape);
    } else if (escape) {
      // Trailing lone backslash: drop it.
      repaired = repaired.slice(0, repaired.length - 1);
    }
    // Strip any trailing partial key-before-colon like `,"foo` (no colon yet).
    // Only relevant when we're inside a string that's actually a key — we
    // can't easily tell here, so we trim a trailing comma-and-key pattern
    // conservatively: if the string closes to produce `..., "key"` with no
    // colon following, JSON.parse would fail. Handle in post-close fallback.
    repaired += '"';
  } else if (stack.length > 0) {
    // Structural drop-off outside any string.
    reason = stack[stack.length - 1] === "]" ? "unterminated-array" : "unterminated-object";
    // Trim any trailing dangling comma or partial key-before-value.
    repaired = _trimDanglingTrailer(repaired);
  }

  // Close all remaining open containers in reverse stack order.
  while (stack.length > 0) {
    repaired += stack.pop();
  }

  try {
    const parsed = JSON.parse(repaired);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return { parsed, reason };
    }
  } catch (_) {
    // Fall through; try one more pass dropping any trailing partial pair.
  }

  const secondPass = _trimToLastGoodPair(repaired);
  if (secondPass != null) {
    try {
      const parsed = JSON.parse(secondPass);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return { parsed, reason };
      }
    } catch (_) {
      // Give up on structural repair.
    }
  }

  return { parsed: null, reason };
}

/**
 * Trim dangling trailers that make otherwise-closable JSON unparseable:
 *   - trailing whitespace
 *   - trailing `,` (object/array tail)
 *   - trailing key-with-colon but no value, e.g. `"foo":`
 *   - trailing key with no colon, e.g. `"foo"`
 * @param {string} text
 */
function _trimDanglingTrailer(text) {
  let out = text.replace(/\s+$/u, "");
  // Trailing `"key":` (no value)
  out = out.replace(/,?\s*"(?:[^"\\]|\\.)*"\s*:\s*$/u, "");
  // Trailing `"key"` (no colon)
  out = out.replace(/,?\s*"(?:[^"\\]|\\.)*"\s*$/u, "");
  // Trailing comma
  out = out.replace(/,\s*$/u, "");
  return out;
}

/**
 * Drop the final (potentially-malformed) key:value pair inside the outermost
 * object and re-close. Used when the first close attempt still fails to parse.
 * Returns null if no safe trim exists.
 * @param {string} text
 */
function _trimToLastGoodPair(text) {
  if (!text.startsWith("{") || !text.endsWith("}")) return null;
  const inner = text.slice(1, -1);
  // Walk backwards looking for a top-level `,` and trim after it.
  let depth = 0;
  let inString = false;
  let escape = false;
  let lastComma = -1;
  for (let i = 0; i < inner.length; i += 1) {
    const ch = inner[i];
    if (inString) {
      if (escape) { escape = false; continue; }
      if (ch === "\\") { escape = true; continue; }
      if (ch === '"') { inString = false; continue; }
      continue;
    }
    if (ch === '"') { inString = true; continue; }
    if (ch === "{" || ch === "[") { depth += 1; continue; }
    if (ch === "}" || ch === "]") { depth -= 1; continue; }
    if (ch === "," && depth === 0) { lastComma = i; }
  }
  if (lastComma < 0) return "{}";
  return "{" + inner.slice(0, lastComma) + "}";
}

/**
 * Last-resort salvage: scan the raw buffer for individual top-level
 * `"key": <value>` pairs whose value is a fully-closed primitive / string /
 * array / object, and assemble them into a new object. Anything ambiguous or
 * truncated is skipped.
 * @param {string} text
 */
function _salvageTopLevelKeys(text) {
  if (text[0] !== "{") return null;
  const inner = text.slice(1);
  const out = {};
  let i = 0;
  let foundAny = false;

  while (i < inner.length) {
    // Skip whitespace and commas.
    while (i < inner.length && /[\s,]/u.test(inner[i])) i += 1;
    if (i >= inner.length) break;
    if (inner[i] !== '"') break;

    // Read key.
    const keyEnd = _findStringEnd(inner, i);
    if (keyEnd < 0) break;
    const rawKey = inner.slice(i + 1, keyEnd);
    const key = _decodeJsonString(rawKey);
    if (key == null) break;

    // Expect colon.
    let j = keyEnd + 1;
    while (j < inner.length && /\s/u.test(inner[j])) j += 1;
    if (inner[j] !== ":") break;
    j += 1;
    while (j < inner.length && /\s/u.test(inner[j])) j += 1;
    if (j >= inner.length) break;

    // Try to read a fully-parseable value starting at j.
    const valueEnd = _findValueEnd(inner, j);
    if (valueEnd < 0) break;
    const rawValue = inner.slice(j, valueEnd + 1);
    try {
      const value = JSON.parse(rawValue);
      out[key] = value;
      foundAny = true;
    } catch (_) {
      break;
    }
    i = valueEnd + 1;
  }

  return foundAny ? out : null;
}

function _findStringEnd(text, openQuoteIdx) {
  let inEscape = false;
  for (let i = openQuoteIdx + 1; i < text.length; i += 1) {
    const ch = text[i];
    if (inEscape) { inEscape = false; continue; }
    if (ch === "\\") { inEscape = true; continue; }
    if (ch === '"') return i;
  }
  return -1;
}

function _findValueEnd(text, start) {
  const ch = text[start];

  if (ch === '"') {
    return _findStringEnd(text, start);
  }
  if (ch === "{" || ch === "[") {
    return _findBalancedEnd(text, start);
  }
  // Primitive: true / false / null / number. Scan until comma / closing brace
  // at depth 0, or end of input.
  for (let i = start; i < text.length; i += 1) {
    const c = text[i];
    if (c === "," || c === "}" || c === "]") {
      return i - 1;
    }
  }
  return -1;
}

function _findBalancedEnd(text, start) {
  const open = text[start];
  const close = open === "{" ? "}" : "]";
  let depth = 0;
  let inString = false;
  let escape = false;
  for (let i = start; i < text.length; i += 1) {
    const ch = text[i];
    if (inString) {
      if (escape) { escape = false; continue; }
      if (ch === "\\") { escape = true; continue; }
      if (ch === '"') { inString = false; continue; }
      continue;
    }
    if (ch === '"') { inString = true; continue; }
    if (ch === open) { depth += 1; continue; }
    if (ch === close) {
      depth -= 1;
      if (depth === 0) return i;
    }
  }
  return -1;
}

function _decodeJsonString(raw) {
  try {
    return JSON.parse(`"${raw}"`);
  } catch (_) {
    return null;
  }
}
